import { useCallback, useState, useEffect } from "react";
import { userService, NotificationSetting } from "@features/settings/service/userService";

export interface NotificationSettings {
  // Email
  emailMentionsReplies: boolean;
  emailProposalUpdates: boolean;
  emailNewMatches: boolean;
  emailWeeklySummary: boolean;
  // In-app
  appEnableSounds: boolean;
  appShowPreviews: boolean;
  appDoNotDisturb: boolean;
  // Marketing
  marketingProductUpdates: boolean;
  marketingNewsletters: boolean;
  marketingResearch: boolean;
}

export const NotificationsTabViewModel = () => {
  const [loading, setLoading] = useState(false);
  const [apiNotifications, setApiNotifications] = useState<NotificationSetting[]>([]);
  const [settings, setSettings] = useState<NotificationSettings>({
    emailMentionsReplies: true,
    emailProposalUpdates: true,
    emailNewMatches: true,
    emailWeeklySummary: false,
    appEnableSounds: false,
    appShowPreviews: true,
    appDoNotDisturb: false,
    marketingProductUpdates: true,
    marketingNewsletters: false,
    marketingResearch: false,
  });

  // Function to fetch notifications
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const notifications = await userService.getNotifications();
      setApiNotifications(notifications);
      // console.log('Fetched notifications:', notifications);
      return notifications;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch notifications from API on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const updateSetting = useCallback(
    (key: keyof NotificationSettings, value: boolean) => {
      setSettings((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleSave = useCallback(async () => {
    setLoading(true);
    try {
      // Save the API notifications with updated enabled status
      await userService.updateNotifications(apiNotifications);
      
      // Refetch notifications to get the latest state from server
      const freshNotifications = await userService.getNotifications();
      setApiNotifications(freshNotifications);
      
      // console.log("Notifications saved and refetched", { settings, apiNotifications: freshNotifications });
    } catch (error) {
      console.error("Failed to save notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [settings, apiNotifications]);

  const handleCancel = useCallback(async () => {
    // Refetch original notifications from server to reset changes
    try {
      await fetchNotifications();
      // console.log("Changes cancelled, notifications reset");
    } catch (error) {
      console.error("Failed to reset notifications:", error);
    }
  }, [fetchNotifications]);

  // Helper to update API notification settings
  const updateApiNotification = useCallback((machineName: string, enabled: boolean) => {
    setApiNotifications(prev => 
      prev.map(notif => 
        notif.machine_name === machineName 
          ? { ...notif, enabled } 
          : notif
      )
    );
  }, []);

  return { 
    loading, 
    settings, 
    updateSetting, 
    handleSave, 
    handleCancel,
    apiNotifications,
    updateApiNotification
  };
};
